/**
 * Cart drawer scroll affordance.
 *
 * Horizon's own <scroll-hint> (assets/scrolling.js) only paints an edge mask on
 * the scroller it wraps, so it cannot say "there is more below" at rest, and
 * anything placed inside it gets masked away too.
 *
 * This element IS the indicator. It is a sibling of the list rather than a
 * wrapper around it, because .cart-drawer__items has to stay a direct flex
 * child of .cart-drawer__content: wrapped, it stops shrinking, the list never
 * scrolls, and the overflow moves up to the drawer body instead.
 *
 * It carries data-can-scroll while the list can still scroll down, and keeps
 * --lf-cart-hint-offset level with the sticky totals. Styling lives in
 * snippets/cart-drawer.liquid.
 */
class LfCartScrollHint extends HTMLElement {
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {HTMLElement | null} */
  #summary = null;
  #queued = false;
  #resizeObserver = new ResizeObserver(() => this.#schedule());
  // The suggestions card fills in after the drawer opens, which grows
  // scrollHeight without resizing the scroller's own box.
  #mutationObserver = new MutationObserver(() => this.#schedule());

  connectedCallback() {
    const content = this.closest('.cart-drawer__content');
    if (!content) return;

    this.#list = content.querySelector('.cart-drawer__items');
    this.#summary = content.querySelector('.cart-drawer__summary');
    if (!this.#list) return;

    this.querySelector('.lf-cart-scroll-hint__button')?.addEventListener('click', this.#scrollDown);
    this.#list.addEventListener('scroll', this.#schedule, { passive: true });
    this.#resizeObserver.observe(this.#list);
    if (this.#summary) this.#resizeObserver.observe(this.#summary);
    this.#mutationObserver.observe(this.#list, { childList: true, subtree: true });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#list?.removeEventListener('scroll', this.#schedule);
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Tapping the chevron scrolls the list on, about three quarters of a screen
   * at a time, so the affordance is usable and not only decorative.
   */
  #scrollDown = () => {
    const list = this.#list;
    if (!list) return;

    const step = Math.max(120, Math.round(list.clientHeight * 0.75));
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({ top: step, behavior: reduced ? 'auto' : 'smooth' });
  };

  #schedule = () => {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#queued = false;
    const list = this.#list;
    if (!list) return;

    if (this.#summary) {
      this.style.setProperty('--lf-cart-hint-offset', `${Math.round(this.#summary.offsetHeight)}px`);
    }

    const remaining = list.scrollHeight - list.scrollTop - list.clientHeight;
    this.toggleAttribute('data-can-scroll', remaining > 4);
  };
}

if (!customElements.get('lf-cart-scroll-hint')) {
  customElements.define('lf-cart-scroll-hint', LfCartScrollHint);
}
